import React, { useState } from 'react';
import { Formik } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { AppDispatch, RootState } from '../../../store/store';
import BackButton from '../../../components/Common/BackButton';
import Loader from '../../../components/Common/Loader';
import { setShowRecreateUserSignup, setStopLoadUpdates } from '../../../store/userSlice';
import { useDispatch, useSelector } from 'react-redux';
import { UserData } from '../../Builder/components/SignUp/SignUpData.interface';
import { signUpNewUser } from '../../Builder/components/SignUp/SignUp.service';
import { CONFIG } from '../../../config/mainConfig';
import Modal from '../../../components/Common/modal/Modal';

// Create a simplified signup schema for email/password only
const SimpleSignUpSchema = () => {
  return Yup.object().shape({
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    phoneNumber: Yup.string().optional(),
    password: Yup.string().required('Password is required').min(8, 'Must be at least 8 characters'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Please confirm your password'),
    confirm: Yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
    marketing: Yup.boolean(),
  });
};

interface GeneralSignUpProps {
  signUpStep?: number;
  setSignUpStep?: (value: number) => void;
  openLoginPageEvent?: () => void;
  plan?: string | null;
}

const GeneralSignUp = ({ signUpStep, setSignUpStep, openLoginPageEvent, plan = null }: GeneralSignUpProps) => {
  const dispatch: AppDispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.user);
  const currentSearchQuery = useSelector((state: RootState) => state.app.currentMainURLQuery);
  const signupViaGoogle = useSelector((state: RootState) => state.user.signupViaGoogle);
  const showRecreateUserSignup = useSelector((state: RootState) => state.user.showRecreateUserSignup);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigateTo = useNavigate();

  const handleSignUpFormSubmit = async (
    email: string,
    values: UserData,
    isRecreateUser: boolean = false,
    resetForm?: () => void,
  ) => {
    setIsLoading(true);
    dispatch(setStopLoadUpdates(true));
    const searchParams = new URLSearchParams(currentSearchQuery);
    signUpNewUser(
      isRecreateUser,
      values,
      searchParams.get(CONFIG.HEADERS.DEMO_ACCESS_KEY),
      searchParams.get(CONFIG.HEADERS.SUBSCRIBE_PLAN_ID),
      searchParams.get(CONFIG.HEADERS.CURRENCY) || undefined,
      searchParams.get(CONFIG.HEADERS.INVITE_ACCESS_KEY),
      signupViaGoogle,
      user,
      setIsLoading,
      dispatch,
      navigateTo,
      resetForm,
    )
      .then()
      .finally(() => {
        setIsLoading(false);
      });
  };

  const recreateUser = (values: UserData, resetForm?: () => void) => {
    handleSignUpFormSubmit(values.email, values, true, resetForm).then().catch();
    dispatch(setShowRecreateUserSignup({ show: false }));
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sofiaSans justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm py-8 px-4 sm:px-10">
        <div className="text-left mb-6">
          <h1 className="text-3xl font-bold text-black mb-3">Bring Your Story to Life!</h1>
        </div>
        <div className="">
          <Formik
            initialValues={{
              firstName: '',
              lastName: '',
              email: '',
              phoneNumber: '',
              password: '',
              confirmPassword: '',
              confirm: false,
              marketing: false,
            }}
            validationSchema={SimpleSignUpSchema}
            onSubmit={(values, { resetForm }) => {
              handleSignUpFormSubmit(values.email, values as UserData, false, resetForm);
            }}
          >
            {({ errors, touched, values, handleChange, handleSubmit, setFieldValue }) => (
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <input
                      type="text"
                      name="firstName"
                      value={values.firstName}
                      onChange={handleChange}
                      placeholder="First Name"
                      className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                    />
                    {touched.firstName && errors.firstName && (
                      <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.firstName}</div>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      name="lastName"
                      value={values.lastName}
                      onChange={handleChange}
                      placeholder="Last Name"
                      className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                    />
                    {touched.lastName && errors.lastName && (
                      <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.lastName}</div>
                    )}
                  </div>
                </div>

                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    placeholder="Email"
                    className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                  />
                  {touched.email && errors.email && (
                    <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.email}</div>
                  )}
                </div>

                <div className="relative">
                  <PhoneInput
                    // country={'us'}
                    value={values.phoneNumber}
                    onChange={(phoneNumber) => setFieldValue('phoneNumber', phoneNumber)}
                    placeholder="Mobile Number (Optional)"
                    inputClass="!w-full !px-4 !pl-12 !py-2 !border !border-secondaryStoryGrayBorder !rounded-lg !text-base !text-secondaryStoryBlackText"
                    containerClass="!w-full"
                    buttonClass="!border !border-secondaryStoryGrayBorder !rounded-l-lg !bg-white hover:!bg-gray-50"
                    dropdownClass="!border !border-secondaryStoryGrayBorder !rounded-lg !shadow-lg"
                    // disableCountryCode={false}
                    // countryCodeEditable={false}
                    // disableCountryGuess={false}
                    preferredCountries={['us', 'ca', 'gb', 'au']}
                    inputStyle={{
                      width: '100%',
                      height: '42px',
                      fontSize: '16px',
                      paddingLeft: '48px',
                      border: '1px solid',
                      borderRadius: '8px',
                      outline: 'none',
                    }}
                    containerStyle={{
                      width: '100%',
                    }}
                    buttonStyle={{
                      border: '1px solid',
                      borderRadius: '8px 0 0 8px',
                      backgroundColor: 'white',
                    }}
                  />
                  {touched.phoneNumber && errors.phoneNumber && (
                    <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.phoneNumber}</div>
                  )}
                </div>

                <div className="relative">
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      placeholder="Password"
                      className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEye : faEyeSlash}
                        className="text-gray-400 hover:text-gray-600"
                      />
                    </button>
                  </div>
                  {touched.password && errors.password && (
                    <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.password}</div>
                  )}
                </div>

                <div className="relative">
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={values.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm Password"
                      className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <FontAwesomeIcon
                        icon={showConfirmPassword ? faEye : faEyeSlash}
                        className="text-gray-400 hover:text-gray-600"
                      />
                    </button>
                  </div>
                  {touched.confirmPassword && errors.confirmPassword && (
                    <div className="absolute text-storyseconderror text-sm mt-0.5">{errors.confirmPassword}</div>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex text-left">
                    <input
                      id="confirm"
                      name="confirm"
                      type="checkbox"
                      checked={values.confirm}
                      onChange={(e) => setFieldValue('confirm', e.target.checked)}
                      className={`h-4 w-4 mt-0.5 focus:ring-secondaryStoryAdditionalLightBlueText rounded border-none text-secondaryStoryAdditionalLightBlueText`}
                    />
                    <label
                      htmlFor="confirm"
                      className={`ml-2 block text-sm ${touched.confirm && errors.confirm ? 'text-storyseconderror' : 'text-secondaryDescriptionTextColor'} `}
                    >
                      I am over 18 or have adult supervision. I agree to the{' '}
                      <Link
                        to="https://buildmystory.com/terms-and-conditions/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-secondaryStoryAdditionalLightBlueText hover:underline"
                      >
                        Terms of Use
                      </Link>{' '}
                      and{' '}
                      <Link
                        to="https://buildmystory.com/privacy-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-secondaryStoryAdditionalLightBlueText hover:underline"
                      >
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                  <div className="flex text-left">
                    <input
                      id="marketing"
                      name="marketing"
                      type="checkbox"
                      checked={values.marketing}
                      onChange={(e) => setFieldValue('marketing', e.target.checked)}
                      className="h-4 w-4 mt-0.5 text-secondaryStoryAdditionalLightBlueText focus:ring-secondaryStoryAdditionalLightBlueText border-secondaryStoryGrayBorder rounded"
                    />
                    <label htmlFor="marketing" className="ml-2 block text-sm text-secondaryDescriptionTextColor">
                      Yes, send me fun activities and tips to inspire my child’s creativity at home.
                    </label>
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full flex items-center justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-base font-bold text-white bg-secondaryStoryAdditionalLightBlueText hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Sign Up
                    <Loader loader={isLoading} />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <BackButton onClick={() => window.history.back()}></BackButton>
                  </div>
                  <Link
                    to="/signin"
                    className="text-secondaryStoryAdditionalLightBlueText hover:underline text-base font-bold"
                  >
                    Log In
                  </Link>
                </div>
                {showRecreateUserSignup && (
                  <Modal
                    title={`Account Already Exists`}
                    className={'global-absolute-popup-style'}
                    description={
                      'We’ve detected an existing account associated with this email address. If you proceed, your existing account will be permanently deleted and replaced with a new one under the invite link you’ve used. Are you sure you want to continue?'
                    }
                    showSubmitButton={false}
                    showCloseButton={false}
                  >
                    <div className="flex justify-center w-full space-x-4 mt-6">
                      <button
                        className="bg-storyblueButton text-white font-medium px-6 py-2 rounded-2xl shadow hover:bg-storyblue transition"
                        onClick={() => recreateUser(values as UserData)}
                      >
                        Yes, Continue
                      </button>

                      <button
                        className="bg-gray-200 text-gray-700 font-medium px-6 py-2 rounded-2xl shadow hover:bg-gray-300 transition"
                        onClick={() => dispatch(setShowRecreateUserSignup({ show: false }))}
                      >
                        Cancel
                      </button>
                    </div>
                  </Modal>
                )}
              </form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default GeneralSignUp;
